import { useEffect, useRef, useState } from 'react';
import { Card, Form, Button, Spinner, Alert, Badge } from 'react-bootstrap';
import type { components } from '@telemedicine/shared';
import { listMessages, sendMessage } from '../services/api';
import { personName } from '../people';
import { BusyButton } from '../components/BusyButton';

type Message = components['schemas']['Message'];

/**
 * Case-scoped doctor↔reviewer conversation. Content is translated async (original preserved).
 * `refreshKey` is bumped by the parent's background poll to re-fetch quietly (no spinner flicker).
 */
export function CaseMessages({
  caseId,
  currentUserId,
  refreshKey,
  onCount,
}: {
  caseId: string;
  /** Viewer's Cognito sub — messages they sent render as "mine" (right); others render left. */
  currentUserId?: string;
  refreshKey?: number;
  /** Report the current item count so the parent can badge this tab when it grows unseen. */
  onCount?: (n: number) => void;
}) {
  const [items, setItems] = useState<Message[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [text, setText] = useState('');
  const [sending, setSending] = useState(false);
  const bottomRef = useRef<HTMLDivElement>(null);

  const refresh = () => {
    listMessages(caseId)
      .then((r) => {
        setItems(r.items);
        onCount?.(r.items.length);
      })
      .catch(() => setError('Failed to load messages'))
      .finally(() => setLoading(false));
  };

  // Re-fetch on case change and on each poll tick. The spinner only shows until the first load
  // completes (loading flips false and stays false), so background refreshes don't flicker.
  useEffect(refresh, [caseId, refreshKey]);

  // Keep the newest message in view (chat-style), whenever the list changes.
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [items]);

  const onSend = async () => {
    if (!text.trim()) return;
    setSending(true);
    setError(null);
    try {
      // Direction is detected from the text server-side (ar<->en), so no sourceLang needed.
      await sendMessage(caseId, { content: text.trim() });
      setText('');
      refresh();
    } catch {
      setError('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  return (
    <Card className="shadow-sm mb-3">
      <Card.Header>Messages</Card.Header>
      <Card.Body>
        {error && <Alert variant="danger" dismissible onClose={() => setError(null)}>{error}</Alert>}

        {loading ? (
          <div className="text-muted d-flex align-items-center">
            <Spinner animation="border" size="sm" className="me-2" /> Loading messages…
          </div>
        ) : items.length === 0 ? (
          <p className="text-muted">No messages yet.</p>
        ) : (
          <div className="mb-3" style={{ maxHeight: 360, overflowY: 'auto' }}>
            {items.map((m) => {
              // "Mine vs everyone else": a message I sent aligns right (accent); anyone else's
              // aligns left (grey) and is labelled name + role — so a reviewer, a fellow local
              // doctor, or an admin are all clearly identified without forcing anyone onto a side.
              const mine = !!currentUserId && m.senderId === currentUserId;
              return (
                <div
                  key={m.messageId}
                  className={`d-flex mb-2 ${mine ? 'justify-content-end' : 'justify-content-start'}`}
                >
                  <div style={{ maxWidth: '80%' }}>
                    {!mine && (
                      <div className="small text-muted mb-1">
                        {personName(m.senderName, m.senderId)}{' '}
                        <Badge bg="secondary" className="align-middle">{m.senderRole}</Badge>
                      </div>
                    )}
                    <div
                      className={`rounded p-2 ${mine ? 'bg-primary text-white' : 'bg-light border'}`}
                    >
                      <div>{m.content.original}</div>
                      {m.content.translated && (
                        <div className={`fst-italic mt-1 ${mine ? 'text-white-50' : 'text-muted'}`}>
                          {m.content.translated}{' '}
                          <Badge bg={mine ? 'light' : 'info'} text={mine ? 'dark' : undefined} className="align-middle">
                            machine translated
                          </Badge>
                        </div>
                      )}
                      {m.content.status === 'PENDING' && (
                        <div className={`small mt-1 ${mine ? 'text-white-50' : 'text-muted'}`}>
                          translation pending…
                        </div>
                      )}
                    </div>
                    <div className={`small text-muted mt-1 ${mine ? 'text-end' : ''}`}>
                      {new Date(m.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>
              );
            })}
            <div ref={bottomRef} />
          </div>
        )}

        <Form.Group>
          <Form.Control
            as="textarea"
            rows={2}
            placeholder="Write a message…"
            value={text}
            onChange={(e) => setText(e.target.value)}
            disabled={sending}
          />
        </Form.Group>
        <div className="mt-2 text-end">
          <BusyButton onClick={onSend} loading={sending} busyLabel="Sending…" disabled={!text.trim()}>
            Send
          </BusyButton>
        </div>
      </Card.Body>
    </Card>
  );
}
